import _ from 'lodash';

import { CoreEnums } from '@/utils/domain/constants';
import BaseFieldHandler from '../BaseFieldHandler';
import { FIELD_SEARCH_TYPES, JSON_DATA_TYPES, REMOTE_DEFAULT_KEYS } from '../constants';
import {
  defaultFieldFilters,
  fieldParentsMap,
  getCurrentUser,
  instanceDataFieldValue,
  mapMultiChoice,
} from '../utils';

const { FormFieldType, FormFieldStringType, EntityQueryOperationType } = CoreEnums;

export default class SelectorFieldHandler extends BaseFieldHandler {
  static supportedTypes = [
    FormFieldStringType.SelectorUser,
    FormFieldStringType.SelectorPosition,
    FormFieldStringType.SelectorUserGroup,
    FormFieldStringType.SelectorArea,
    FormFieldStringType.WebServiceRemoteSearch,
    FormFieldStringType.WebServiceRemoteList,
    FormFieldStringType.FieldDynamicEntityList,
    FormFieldStringType.DataBaseRemoteList,
    FormFieldStringType.DataBaseRemoteSearch,
    FormFieldStringType.FieldDynamicEntitySearch,
    FormFieldType.SelectorUser,
    FormFieldType.SelectorPosition,
    FormFieldType.SelectorUserGroup,
    FormFieldType.SelectorArea,
    FormFieldType.WebServiceRemoteSearch,
    FormFieldType.WebServiceRemoteList,
    FormFieldType.FieldDynamicEntityList,
    FormFieldType.DataBaseRemoteList,
    FormFieldType.DataBaseRemoteSearch,
    FormFieldType.FieldDynamicEntitySearch,
  ];

  handle() {
    const config = this.getBaseConfig();
    const { isDetails, isLocked } = this.fieldState;
    const { isCustomList, isDynamicEntity, isContinue } = this.routeInfo;

    const {
      id,
      type,
      filters = [],
      allowMultiple,
      allowInsertNewOption,
      autoSelect,
      autoSelectAll,
      maxSelectionsAllowed,
      minSelectionsRequired,
      displayTextDynamicEntityField,
      userDisplayValue,
      userExtraFieldId,
      userExtraField,
      currentUser,
      operationType = EntityQueryOperationType.EntityForm,
      formId,
    } = this.baseConfig;

    const parents =
      !isLocked && !isDetails && this.dependency ? fieldParentsMap(filters, this.configData) : [];
    const deeps = this.getDependencies();
    const isObject = !allowMultiple && (_.isObject(config.value) || this.isJsonLike(config.value));
    const isRemoteSearch = FIELD_SEARCH_TYPES.includes(type);

    const fieldTypeConfigs = this.getFieldTypeConfigs(
      type,
      id,
      displayTextDynamicEntityField,
      userDisplayValue,
      userExtraFieldId,
    );

    const fieldConfig = {
      ...config,
      type: isDetails && !allowMultiple ? 'text' : 'select',
      labelInValue: !this.dependency,

      ...(!isDetails && {
        locale: false,
        ...fieldTypeConfigs,
        ...REMOTE_DEFAULT_KEYS,
        httpMethod: 'post',
        apiRoute: `field/getData/${id}/${operationType}`,

        ...(isCustomList && { isCustomList: true }),
        ...(isLocked && { showSearch: false, allowClear: false }),
        ...(parents?.length > 0 && { parents, relationships: true }),
        ...(isRemoteSearch && { isRemoteSearch }),
        ...(allowInsertNewOption && { allowInsertOptionManually: allowInsertNewOption }),
        ...(isDynamicEntity && { isDynamicEntity: true, entityId: isDynamicEntity.params.id }),
        ...(JSON_DATA_TYPES.includes(type) && { jsonSelectData: true, labelInValue: true }),
        ...(filters?.length > 0 && defaultFieldFilters(filters, this.configData, formId)),
        ...(this.dependency && !isRemoteSearch && { isAutoSelectItemEnabled: autoSelect }),
        ...(deeps?.length > 0 && { deeps, labelInValue: true }),
        ...this.getCommonFormRouteProps(),
      }),

      ...(allowMultiple && {
        mode: 'multiple',
        ...(!isDetails && !this.baseConfig.readOnly && { maxTagCount: 3, maxTagTextLength: 15 }),
        ...(!isRemoteSearch && { isAutoSelectAllEnabled: autoSelectAll }),
        value: mapMultiChoice(config.value),
        ...(maxSelectionsAllowed > 1 && { maxSelectionsAllowed }),
        ...(minSelectionsRequired > 1 && { minSelectionsRequired }),
      }),

      ...(isObject && {
        value: instanceDataFieldValue(
          JSON_DATA_TYPES.includes(type),
          config.value,
          this.dependency,
          isDetails,
        ),
      }),
    };

    if (isContinue && !isLocked && currentUser) {
      fieldConfig.value = getCurrentUser(type, { userDisplayValue, userExtraField });
    }

    return fieldConfig;
  }

  getFieldTypeConfigs(type, id, displayTextDynamicEntityField, userDisplayValue, userExtraFieldId) {
    const toEntityList = {
      dynamicEntityFieldId: id,
      displayConfig: displayTextDynamicEntityField,
    };

    const toSelUser = {
      itemDb: 'sourceUser.id',
      dynamicEntityFieldId: id,
      userDisplayValue,
      userExtraFieldId,
      user: true,
    };

    const configs = {
      [FormFieldStringType.SelectorArea]: { area: true },
      [FormFieldStringType.SelectorPosition]: { position: true },
      [FormFieldStringType.SelectorUserGroup]: { userGroup: true },
      [FormFieldStringType.FieldDynamicEntityList]: toEntityList,
      [FormFieldStringType.FieldDynamicEntitySearch]: toEntityList,
      [FormFieldStringType.SelectorUser]: toSelUser,
      [FormFieldType.SelectorArea]: { area: true },
      [FormFieldType.SelectorPosition]: { position: true },
      [FormFieldType.SelectorUserGroup]: { userGroup: true },
      [FormFieldType.FieldDynamicEntityList]: toEntityList,
      [FormFieldType.FieldDynamicEntitySearch]: toEntityList,
      [FormFieldType.SelectorUser]: toSelUser,
    };

    return configs[type] || {};
  }
}
